import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';

export interface RecurringInvoiceLineItem {
  description: string;
  quantity: string;
  unit_amount: string;
}

export interface RecurringInvoiceTemplate {
  id: number;
  name: string;
  client_id: number;
  project_id: number | null;
  line_items: RecurringInvoiceLineItem[];
  tax_amount: string;
  notes: string | null;
  net_terms_days: number;
  rrule: string;
  starts_at: number;
  ends_at: number | null;
  next_due_at: number | null;
  is_active: boolean;
  created_at: number;
  updated_at: number;
  client_name?: string;
  client_company_name?: string | null;
  project_case_number?: string | null;
}

export function useRecurringInvoices() {
  return useQuery({
    queryKey: ['recurring-invoices'],
    queryFn: ({ signal }) =>
      api.get<{ recurring_invoice_templates: RecurringInvoiceTemplate[] }>(
        '/api/recurring-invoices', signal),
  });
}

export interface CreateRecurringInvoiceInput {
  name: string;
  client_id: number;
  project_id?: number;
  line_items: RecurringInvoiceLineItem[];
  tax_amount?: string;
  notes?: string;
  net_terms_days?: number;
  rrule: string;
  starts_at: number;
  ends_at?: number | null;
  is_active?: boolean;
}

export function useCreateRecurringInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRecurringInvoiceInput) =>
      api.post<{ recurring_invoice_template: RecurringInvoiceTemplate; generated_count?: number }>(
        '/api/recurring-invoices', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['recurring-invoices'] });
      // Create may backfill past-due draft invoices immediately.
      void qc.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

export interface UpdateRecurringInvoiceInput {
  id: number;
  is_active?: boolean;
  name?: string;
  rrule?: string;
  starts_at?: number;
  ends_at?: number | null;
}

export function useUpdateRecurringInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateRecurringInvoiceInput) =>
      api.patch<{ recurring_invoice_template: RecurringInvoiceTemplate }>(
        `/api/recurring-invoices/${id}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['recurring-invoices'] });
    },
  });
}

export function useDeleteRecurringInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/recurring-invoices/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['recurring-invoices'] });
    },
  });
}
